import Checkbox from '@material-ui/core/Checkbox'
import { ComponentLikeType } from 'elements/hardwareSelectorV2/types'
import { BaseTheme, Box, Grid, Skeleton, styled } from 'opensolar-ui'
import ProductTile from 'pages/ordering/catalogv3/ProductTile'
import { Fragment } from 'react'

const BLUE_BG = BaseTheme.palette.blue[300]
const BLUE_TEXT = BaseTheme.palette.blue[1000]
const PURPLE = BaseTheme.palette.purple[700]
const PURPLE_BG = `${PURPLE}1A`

export const BundleRow = styled(Grid)({
  display: 'flex',
  gap: 16,
  overflowX: 'auto',
})

const TileWrapper = styled('div')(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  alignItems: 'center',
  border: `1px solid ${theme.palette.grey[100]}`,
  borderRadius: theme.shape.borderRadius,
}))

const LabelBadge = styled('span')<{ variant: 'this' | 'recommended' }>(({ variant }) => ({
  fontSize: 10,
  fontWeight: 700,
  textTransform: 'uppercase' as const,
  letterSpacing: '0.5px',
  borderRadius: 4,
  padding: '3px 8px',
  marginBottom: 6,
  ...(variant === 'this' ? { background: BLUE_BG, color: BLUE_TEXT } : { background: PURPLE_BG, color: PURPLE }),
}))

const TileCheckbox = styled('div')({
  alignSelf: 'flex-end',
})

const PlusIcon = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  height: 'stretch',
  fontSize: theme.typography.h1.fontSize,
  fontWeight: theme.typography.fontWeightLight,
  color: theme.palette.grey[500],
}))

export type ProductBundleRowProps = {
  products: ComponentLikeType[]
  isFetching: boolean
  bundleCodes: string[]
  distributorKey: string
  currentProductCode?: string
  otherLabel: 'Required' | 'Recommended'
  allowSelection: boolean
  selectedProducts: Set<string>
  onToggleProduct: (code: string) => void
}

/** Shared bundle product row — used by BundleSection and BundleDealModal. */
export const ProductBundleRow = ({
  products,
  isFetching,
  bundleCodes,
  distributorKey,
  currentProductCode,
  otherLabel,
  allowSelection,
  selectedProducts,
  onToggleProduct,
}: ProductBundleRowProps) => {
  if (isFetching) {
    return (
      <>
        {bundleCodes.map((_, i) => (
          <Grid item key={i}>
            <TileWrapper>
              <Skeleton width={236} height={500} variant="rounded" />
            </TileWrapper>
          </Grid>
        ))}
      </>
    )
  }

  return (
    <>
      {products.map((product, index) => {
        const isCurrentProduct = currentProductCode === product.code
        const isSelected = selectedProducts.has(product.code)
        const component = {
          ...product,
          ordering_v2: product.ordering_v2?.filter((d) => d.distributor === distributorKey),
        }
        return (
          <Fragment key={product.id}>
            <Grid item style={{ marginBottom: '2px' }}>
              <TileWrapper>
                <Box display="flex" alignItems="center" justifyContent="space-between" width="95%" padding="8px 16px">
                  <LabelBadge variant={isCurrentProduct ? 'this' : 'recommended'}>
                    {isCurrentProduct ? 'This Item' : otherLabel}
                  </LabelBadge>

                  {isCurrentProduct && (
                    <TileCheckbox>
                      <Checkbox checked disabled size="small" style={{ color: BLUE_TEXT, padding: 2 }} />
                    </TileCheckbox>
                  )}
                  {!isCurrentProduct && (
                    <TileCheckbox>
                      <Checkbox
                        checked={isSelected}
                        onChange={() => onToggleProduct(product.code)}
                        size="small"
                        style={{ color: BLUE_TEXT, padding: 2 }}
                        disabled={!allowSelection}
                      />
                    </TileCheckbox>
                  )}
                </Box>

                <ProductTile component={component} liteMode />
              </TileWrapper>
            </Grid>
            {index < products.length - 1 && (
              <Grid item>
                <PlusIcon>+</PlusIcon>
              </Grid>
            )}
          </Fragment>
        )
      })}
    </>
  )
}

export default ProductBundleRow
